import React, { useMemo } from 'react';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  ConditionsIcon,
  GoHighLevelIcon,
  KlaviyoIcon,
  SalesForceIcon,
  SendlaneIcon,
  ShopifyIcon,
  WebhookIcon,
} from '@mm/icons';
import { UnicornDetectorIcon } from '@mm/icons/AntDesignIcons';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import {
  EAutomationAction,
  EAutomationActionCategory,
  EAutomationActionCategoryType,
  IAutomationAction as IActionsList,
} from '@mm/state/model/AutomationModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  getAutomationAction,
  getGraphLibInstance,
} from '@mm/state/selectors/automations';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as GoogleSheetsIcon } from 'assets/google-sheets-logo-outlined.svg';
import { ReactComponent as ZapierIcon } from 'assets/icons/zapier-logomark.svg';
import { ReactComponent as FacebookIcon } from 'assets/facebook-circle.svg';
import { ReactComponent as MailchimpIcon } from 'assets/mailchimp.svg';
import { ReactComponent as SOLDIcon } from 'assets/sold-icon.svg';
import { useSelector } from 'react-redux';
import { timeout } from '../../../../../../common/helpers/utils';
import { useConnectionWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { useDelayWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-delay-widget-handlers';
import { useNavigateWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-navigate-widget-handlers';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ConditionIcon } from './DialogueEditor/NodeTypes/Condition/Condition';
import { DelayIcon } from './DialogueEditor/NodeTypes/Delay/Delay';
import { EmailDialogueIcon } from './DialogueEditor/NodeTypes/Dialogue/Dialogue';
import { canUseFacebookAdsSyncIntegrationAndWidget } from '@mm/state/selectors/adAccountsSelecotrs';
import { TagIcon } from './DialogueEditor/NodeTypes/Tag/Tag';
import { useTagsWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-tags-widget-handlers';
import { useCommonWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';

export interface IAutomationAction {
  type: WidgetTypes;
  id: EAutomationAction;
  name: string;
  description?: string;
  category: EAutomationActionCategoryType;
  add?: (
    dialogueId: number,
    sortOrder: number,
    pageGroupId?: number,
    widgetId?: number,
    position?: 'before' | 'after',
    isUnicornPath?: boolean,
  ) => Promise<WidgetModel>;
  icon: typeof ConditionIcon | typeof UnicornDetectorIcon;
  disabled?: boolean;
  iconColor?: string;
  className?: string;
}

export const useAutomationActionList = (
  props: IActionsList & {
    campaignId: number;
    visibleActionsFilterFunc?: (id: EAutomationAction) => boolean;
  },
): IAutomationAction[] => {
  const { campaignId, visibleActionsFilterFunc: byVisibility = () => true } =
    props;
  const botId = useSelector(activeChatbotIdSelector);
  const navigateWidgetHandlers = useNavigateWidgetHandlers();
  const delayWidgetHandlers = useDelayWidgetHandlers();
  const tagWidgetHandlers = useTagsWidgetHandlers();
  const widgetCRUDActions = useWidgetCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const modalActions = useModalActions();
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const { isSmsSupported } = usePhoneNumber();
  const fbPage = useSelector(activeFacebookPageSelector);
  const isEmailActivated = !!fbPage?.emailActivated;

  const pageCRUDActions = usePageCRUDActions();
  const { openFreeTrialUpgradeModalPromotional } =
    useFreeTrialUpgradeModal(fbPage);

  const graphLibInstance = useSelector(getGraphLibInstance);

  const automationAction = useSelector(getAutomationAction);
  const planFeatureFlags = useSelector(combinedFeatureFlagsSelector);

  const createConditionPages = async (
    pageGroupId?: number,
    showHidden?: boolean,
  ) => {
    const no = await pageCRUDActions.create(
      {
        name: 'Yes Dialogue',
        pageGroupId,
        botId,
        parentId: campaignId,
        parentType: 'campaign',
      },
      showHidden,
    );

    const yes = await pageCRUDActions.create(
      {
        name: 'No Dialogue',
        botId,
        parentId: campaignId,
        parentType: 'campaign',
      },
      showHidden,
    );
    return { no, yes };
  };

  const canUseFacebookAds = useSelector(
    canUseFacebookAdsSyncIntegrationAndWidget,
  );

  return useMemo(() => {
    const baseActions = [
      {
        type: WidgetTypes.DELAY,
        id: EAutomationAction.WAIT,
        name: 'Wait',
        icon: DelayIcon,
        iconColor: '#FA8C16',
        add: async (dialogueId: number, sortOrder: number) =>
          delayWidgetHandlers.addNew(dialogueId, sortOrder),
        requiredFeature: 'delayWidget' as const,
        category: EAutomationActionCategory.rules,
      },
      {
        type: WidgetTypes.NAVIGATE,
        name: 'Send Email',
        id: EAutomationAction.SEND_EMAIL,
        icon: EmailDialogueIcon,
        iconColor: '#F5222D',
        category: EAutomationActionCategory.message,
        add: async (dialogueId, sortOrder, pageGroupId) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SEND_EMAIL_ACTION,
            });
            return;
          }
          if (!isEmailActivated) {
            modalActions.openActivateEmailAccountModal();
            return;
          }
          const res = await pageCRUDActions.create({
            name: `Email Message`,
            botId,
            pageGroupId,
            platforms: ['email'],
            parentId: campaignId,
            parentType: 'campaign',
          });
          await widgetCRUDActions.create({
            config: {
              kind: 'to_contact',
            },
            pageId: res.page.id,
            sortOrder: 0,
            type: 'email',
          });
          return navigateWidgetHandlers.addNavigate({
            dialogueId,
            sortOrder,
            associatedPageId: res.page.id,
            oppositePageId: null,
            sendNextAfterValue: 5,
            sendNextAfterMeasure: 'minute',
          });
        },
      },
      {
        type: WidgetTypes.EMAIL,
        name: 'S.o.L.D. Email',
        id: EAutomationAction.SEND_EMAIL,
        icon: SOLDIcon,
        iconColor: '#FA8C16',
        category: EAutomationActionCategory.message,
        add: async (dialogueId, sortOrder, pageGroupId) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SEND_SOLD_EMAIL_ACTION,
            });
            return;
          }
          if (!isEmailActivated) {
            modalActions.openActivateEmailAccountModal();
            return;
          }

          const newWidget = {
            botId,
            pageGroupId,
            platforms: ['email'],
            parentId: campaignId,
            parentType: 'campaign',
            config: {
              kind: 'to_contact',
            },
            soldConfigurations: {
              defaultConfiguration: true,
              donkey: { notOpen: null, notClick: null, notReply: null },
              unicorn: {
                open: null,
                click: null,
                reply: null,
                notifications: {
                  defaultConfiguration: true,
                  rule: {
                    open: null,
                    click: null,
                    reply: null,
                  },
                },
              },
            },
            pageId: dialogueId,
            sortOrder,
            type: 'email',
          };

          return commonWidgetHandlers.create(newWidget, dialogueId, sortOrder);
        },
      },
      {
        type: WidgetTypes.TAGS,
        id: EAutomationAction.TAG,
        name: 'Tags',
        icon: TagIcon,
        iconColor: '#8C8C8C',
        category: EAutomationActionCategory.rules,
        add: async (dialogueId: number, sortOrder: number) =>
          tagWidgetHandlers.addNew(dialogueId, sortOrder),
      },
      {
        type: WidgetTypes.NAVIGATE,
        name: 'Condition',
        id: EAutomationAction.CONDITION,
        icon: ConditionsIcon,
        iconColor: '#096DD9',
        category: EAutomationActionCategory.rules,
        add: async (
          dialogueId: number,
          sortOrder: number,
          pageGroupId,
          widgetId?: number,
          position = 'before' as 'before' | 'after',
        ) => {
          // split existing path
          if (widgetId) {
            try {
              return await widgetCRUDActions.splitPageUsingGoToWidget({
                botId,
                widgetId,
                pageId: dialogueId,
                subtype: 'condition',
                branch: 'yes',
                position,
              });
              // handle later
            } catch (err) {
              console.log('Condition splitPageUsingGoToWidget error: ', err);
            }
            return;
          }

          // Handing cases when no widgets exist, like start of the automation
          const { yes, no } = await createConditionPages(pageGroupId);

          await timeout(500);

          return navigateWidgetHandlers.addNavigate({
            dialogueId,
            sortOrder,
            associatedPageId: yes.page.id,
            oppositePageId: no.page.id,
            sendNextAfterValue: 5,
            sendNextAfterMeasure: 'minute',
          });
        },
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Klaviyo',
        id: EAutomationAction.KLAVIYO,
        description: 'Description',
        icon: KlaviyoIcon,
        iconColor: '#262626',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_KLAVIYO_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'klaviyo',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Shopify',
        id: EAutomationAction.SHOPIFY,
        description: 'Description',
        icon: ShopifyIcon,
        iconColor: '#8cb83b',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SHOPIFY_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'shopify',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Salesforce',
        id: EAutomationAction.SALESFORCE,
        description: 'Description',
        icon: SalesForceIcon,
        iconColor: '#00A1E0',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SALESFORCE_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'salesforce',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      ...(canUseFacebookAds
        ? [
            {
              type: WidgetTypes.SEND_DATA,
              name: 'Facebook',
              id: EAutomationAction.FACEBOOK_ADS,
              description: ' ',
              icon: FacebookIcon,
              iconColor: '#5267A7',
              category: EAutomationActionCategory.crm,
              add: async (dialogueId: number, sortOrder: number) => {
                if (!planFeatureFlags.nativeIntegrations) {
                  openFreeTrialUpgradeModalPromotional({
                    landmineName:
                      landmineNames.AUTOMATIONS_FACEBOOK_AD_AUDIENCE_SYNC_ACTION,
                  });
                  return;
                }
                return connectionWidgetHandlers.addNew(
                  dialogueId,
                  sortOrder,
                  'facebook_ads_audience',
                );
              },
              requiredFeature: 'nativeIntegrations' as const,
            },
          ]
        : []),
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Google Sheet',
        id: EAutomationAction.GOOGLE_SHEET,
        description: 'Description',
        icon: GoogleSheetsIcon,
        iconColor: '#389E0D',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_GOOGLE_SHEETS_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'google_sheets',
          );
        },

        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Sendlane',
        id: EAutomationAction.SENDLANE,
        description: 'Description',
        icon: SendlaneIcon,
        iconColor: '#FF3C78',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SENDLANE_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'sendlane',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'HighLevel',
        id: EAutomationAction.HIGHLEVEL,
        description: 'Description',
        icon: () => <GoHighLevelIcon size={32} />,
        iconColor: '#000',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_HIGHLEVEL_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'gohighlevel',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        id: EAutomationAction.ZAPIER,
        name: 'Zapier',
        description: 'Description',
        icon: ZapierIcon,
        iconColor: '#FF4F00',
        className: 'zapier',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.zapier) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_ZAPIER_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'zapier',
            '',
          );
        },
        requiredFeature: 'zapier' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        id: EAutomationAction.JSON_WEBHOOK,
        name: 'JSON Webhook',
        description: 'Description',
        icon: WebhookIcon,
        iconColor: '#40A9FF',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.jsonWebhook) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_SEND_JSON_WEBHOOK_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'zapier',
          );
        },
        requiredFeature: 'zapier' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'MailChimp',
        id: EAutomationAction.MAILCHIMP,
        description: 'Description',
        icon: MailchimpIcon,
        iconColor: '#000',
        category: EAutomationActionCategory.crm,
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional({
              landmineName: landmineNames.AUTOMATIONS_MAILCHIMP_ACTION,
            });
            return;
          }
          return connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'mailchimp',
          );
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
    ].filter(Boolean) as IAutomationAction[];

    const getAllInPath = (
      direction: 'predecessors' | 'successors',
      nodes: string[],
    ) => {
      const allNodes = nodes.reduce((acc, cur) => {
        return [
          ...acc,
          cur,
          ...getAllInPath(
            direction,
            graphLibInstance?.[direction](String(cur)) || [],
          ),
        ];
      }, []);
      return allNodes;
    };

    const self = graphLibInstance?.node(String(props.widgetId));

    const isConditionOrUnicorn =
      self?.data?.widget?.type === 'go_to' &&
      !!self?.data?.widget?.config?.pageId &&
      !!self?.data?.widget?.config?.oppositePageId;

    const successorsToVisitIds =
      props.position === 'before'
        ? [
            String(props.widgetId),
            ...(graphLibInstance?.successors(String(props.widgetId)) || []),
          ]
        : graphLibInstance?.successors(String(props.widgetId)) || [];

    const successorNodesToVisit = successorsToVisitIds
      .map(id => graphLibInstance?.node(id))
      .filter(n => !!n);

    const nodeToFollow = successorNodesToVisit.find(
      n => n?.data?.widget?.pageId === props.dialogueId,
    );

    const selfAndSuccessors = getAllInPath(
      'successors',
      nodeToFollow
        ? [String(nodeToFollow.id as string)]
        : isConditionOrUnicorn
        ? []
        : graphLibInstance?.successors(String(props.widgetId)) || [],
    );

    const hasUnicornsBelow = selfAndSuccessors.reduce((acc, cur) => {
      if (acc) return true;
      const node = graphLibInstance?.node(cur);
      return (
        node?.data?.widget?.type === 'go_to' &&
        node?.data?.widget?.config?.subtype === 'unicorn_detector'
      );
    }, false);

    const findIfInUnicornPath = (nodeToVisitId: string) => {
      const predecessorsIds = graphLibInstance?.predecessors(nodeToVisitId);
      const currentNode = graphLibInstance?.node(nodeToVisitId);

      let isUnicornPath = false;

      // Handle initial case
      if (
        currentNode &&
        nodeToVisitId === String(props.widgetId) &&
        props.position === 'after'
      ) {
        const isCurrentUnicornDetector =
          currentNode?.data?.widget?.type === 'go_to' &&
          currentNode?.data?.widget?.config?.subtype === 'unicorn_detector';

        if (isCurrentUnicornDetector) {
          const isEnteringUnicornPath =
            currentNode?.data?.widget?.config?.pageId === props.dialogueId;

          if (isEnteringUnicornPath) return true;
        }
      }

      if (!currentNode || !predecessorsIds?.length) {
        return isUnicornPath;
      }

      predecessorsIds?.forEach(id => {
        const predecessorNode = graphLibInstance?.node(id);

        const isPredecessorUnicornDetector =
          predecessorNode?.data?.widget?.type === 'go_to' &&
          predecessorNode?.data?.widget?.config?.subtype === 'unicorn_detector';

        if (isPredecessorUnicornDetector) {
          const isEnteringUnicornPath =
            currentNode?.data?.widget?.pageId ===
            predecessorNode?.data?.widget?.config?.pageId;

          if (isEnteringUnicornPath) {
            isUnicornPath = true;
            return;
          }
        }

        const isUnicornUpstream = findIfInUnicornPath(id);

        if (isUnicornUpstream) {
          isUnicornPath = true;
        }
      });

      return isUnicornPath;
    };

    const isInUnicornPath = self?.id ? findIfInUnicornPath(self.id) : false;

    if (isInUnicornPath || props.isUnicornPath) {
      return baseActions;
    }

    const unicornDetectorAction = {
      type: WidgetTypes.NAVIGATE,
      name: 'Unicorn Detector',
      id: EAutomationAction.UNICORN_DETECTOR,
      icon: UnicornDetectorIcon,
      iconBgColor: '#E6F7FF',
      iconColor: '#40A9FF',
      category: EAutomationActionCategory.rules,
      add: async (
        dialogueId: number,
        sortOrder: number,
        pageGroupId,
        widgetId?: number,
        position = 'before' as 'before' | 'after',
      ) => {
        // split existing path
        if (widgetId) {
          try {
            const res = await widgetCRUDActions.splitPageUsingGoToWidget({
              botId,
              widgetId,
              pageId: dialogueId,
              subtype: 'unicorn_detector',
              branch: hasUnicornsBelow ? 'no' : 'yes',
              position,
            });
            return res;
          } catch (err) {
            console.log(
              'Unicorn detector splitPageUsingGoToWidget error: ',
              err,
            );
          }
          return;
        }

        // Handing cases when no widgets exist, like start of the automation
        const { yes, no } = await createConditionPages(pageGroupId, true);

        await timeout(500);

        const nav = await navigateWidgetHandlers.addNavigate({
          dialogueId,
          sortOrder,
          associatedPageId: yes.page.id,
          oppositePageId: no.page.id,
          sendNextAfterValue: 5,
          sendNextAfterMeasure: 'minute',
          subtype: 'unicorn_detector',
        });

        await timeout(500);

        await Promise.all([
          widgetCRUDActions.retrieveAll(yes.page.id),
          widgetCRUDActions.retrieveAll(no.page.id),
        ]);

        return nav;
      },
    } as IAutomationAction;

    return baseActions.filter(action => byVisibility(action.id));
  }, [automationAction, botId, isSmsSupported, canUseFacebookAds]);
};
